/**
 * The SNS topic — the input without which none of this slice does anything, and
 * which had no field on this screen until now.
 *
 * ## The dead end this exists to close
 *
 * Amazon reports opens and clicks **only** through a configuration set's event
 * destination, and an event destination has to publish somewhere. Wend's answer
 * is an SNS topic the tenant owns, subscribed to this workspace's webhook URL.
 * Until one is nominated the API cannot finish provisioning, and it says so —
 * `NEEDS_TOPIC_MESSAGE` in `tracking.service.ts`, which arrives here as a 409
 * and ends with *"…and paste its ARN here"*.
 *
 * There was no **here**. The message named an affordance this screen did not
 * have, so pressing "Set up tracking in my AWS account" produced a paragraph
 * telling somebody to do something the product gave them no way to do. That is
 * the precise failure mode decision 1 of this slice exists to prevent — *a
 * setup step a tenant performs by hand fails silently and surfaces as "why are
 * there no numbers" three weeks later* — arriving through the door that was
 * built to stop it. The API's own comment on that constant says the repair is a
 * form field on the same screen. This is the form field.
 *
 * ## Why the tenant supplies the topic and Wend does not create one
 *
 * Creating a topic means asking for `sns:CreateTopic` and `sns:Subscribe`
 * across a customer's AWS account — a materially wider grant than "send mail as
 * this identity", for a step most tenants already performed in slice 9 to get
 * bounces and complaints. So the division is: Wend creates the configuration
 * set, its event destination and its tracking options, because those cannot be
 * built by hand without going silently wrong; the tenant supplies the topic,
 * because that part can be, and usually already has been.
 *
 * That is a real asymmetry with decision 1 rather than a violation of it. The
 * decision is about steps that *fail silently*. A missing topic does not fail
 * silently — it refuses provisioning outright, on this screen, with the reason
 * and now with the field.
 *
 * ## This panel cannot tell you whether a topic is already nominated
 *
 * `TrackingResponse` does not carry `snsTopicArn`. The API's internal envelope
 * does, and `toTrackingResponse` in the controller strips it before answering,
 * so a browser genuinely cannot know the current value. Three options were
 * considered:
 *
 *   1. render nothing until a provisioning failure proves a topic is missing —
 *      rejected, because it hides the only control that fixes the most common
 *      first-run failure behind having already hit that failure;
 *   2. guess from `configurationSet.provisioned` — rejected, because
 *      unprovisioned has several causes and "your topic is missing" would be
 *      wrong for most of them, in a confident voice;
 *   3. show the field whenever SES is connected, phrased so it reads correctly
 *      whether or not a topic is set, and never claim a state it cannot see.
 *
 * Three, which is why the input starts empty rather than pre-filled, why the
 * heading is a question about setup rather than a status, and why nothing here
 * renders a tick. **The field is write-only from the browser's point of view,
 * and the copy is written so that is honest rather than concealed.** The report
 * for this slice asks for `snsTopicArn` on `TrackingResponse` so this panel can
 * stop being coy; it is a one-field change on a `.strict()` schema and it
 * belongs to whoever owns `packages/shared`.
 *
 * ## The webhook URL is on this panel and not somewhere else
 *
 * The refusal says *"subscribe it to the webhook URL on this screen"*, and that
 * URL was not on this screen either — `GET /webhooks/ses/url` existed with no
 * caller in the web app at all. It is here, beside the ARN field, because the
 * two are one task performed in one visit to the AWS console: create or pick a
 * topic, subscribe it to this URL, paste the ARN back. Putting the URL in a
 * different card would make somebody hold it in their head across a scroll.
 *
 * It is fetched rather than derived: the token in it comes from
 * `WebhookTokenService`, and a frontend that assembled it from a tenant id
 * would be handing somebody a URL that silently never receives anything.
 *
 * ## Slice 19: the topic is for delivery reports first
 *
 * Opens and clicks were this panel's whole subject, and they are the smaller
 * half of it now. Saving a topic sets up **delivery reports** — a second
 * configuration set, which untracked email goes through and which adds nothing
 * to a message — so a workspace that never switches tracking on still learns
 * what was delivered, what bounced and who complained. Until slice 19 such a
 * workspace could save a topic here and never receive one report.
 *
 * Whether delivery reports are set up is a fact the browser *can* see —
 * `settings.deliveryReports` travels on every tracking response — so
 * {@link DeliveryReportsStatus} states it as a status rather than hedging the
 * way the topic field has to. What it still cannot see is whether the topic's
 * subscription is confirmed, and the copy says so rather than letting "set up"
 * read as "arriving".
 */
import type { DeliveryReportsSet, TrackingResponse } from '@wend/shared';
import { Button, Callout, FormField, Input, Spinner } from '@wend/ui';
import { useState, type FormEvent } from 'react';

import { ErrorState } from '../../components/ErrorState';
import { ROOT_FIELD, apiFieldErrors } from '../../lib/api/errors';
import { formatDateTime } from '../../lib/format';
import { validate, withoutError, type FormErrors } from '../../lib/form';
import { useAsyncData } from '../../lib/use-async-data';
import { FormAlert } from '../../components/FormAlert';
import { CopyableValue } from './CopyableValue';
import { RequestFailureAlert, requestFailure, type RequestFailure } from './request-failure';
import { readDeliveryReports } from './tracking-display';
import {
  fetchTracking,
  fetchWebhookUrl,
  setTrackingTopic,
  setUpDeliveryReports,
  trackingTopicRequestSchema,
} from './tracking-api';

/**
 * After a refused write, the panel reads the settings again rather than keep the
 * ones it was showing. A refusal can still have stored something — a topic saved
 * before a conflict, a delivery reports set Wend stopped using because it tracks
 * — and a panel left on its old state went on saying "Delivery reports are set
 * up … adds nothing to the message" beside the refusal that had just stopped
 * using the set (review). Best effort: when the read fails too, the refusal is
 * already on screen and says enough.
 */
async function reloadAfterRefusal(onChanged: (next: TrackingResponse) => void): Promise<void> {
  try {
    onChanged(await fetchTracking());
  } catch {
    // The caller is already showing the failure that brought it here.
  }
}

export interface SnsTopicPanelProps {
  readonly onChanged: (next: TrackingResponse) => void;
  /** True while another control on the card is writing. */
  readonly busy?: boolean;
  /**
   * True when the configuration set is not provisioned — which a missing topic
   * is the most common cause of, though not the only one. Raises the panel's
   * prominence; never used to *assert* that the topic is what is wrong.
   */
  readonly unprovisioned: boolean;
  /** Slice 19's second configuration set, straight off the settings. */
  readonly deliveryReports: DeliveryReportsSet;
}

export function SnsTopicPanel({
  onChanged,
  busy = false,
  unprovisioned,
  deliveryReports,
}: SnsTopicPanelProps) {
  /** Raised by whichever control is writing; see the comment beside the status. */
  const [working, setWorking] = useState(false);

  return (
    <section className="flex flex-col gap-4 border-t border-line pt-5">
      <div className="flex flex-col gap-1">
        <h3 className="text-[0.98rem] font-medium text-text">
          Where Amazon sends your delivery reports, opens and clicks
        </h3>
        <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
          Amazon reports deliveries, bounces and complaints — and opens and clicks, if you switch
          tracking on — only through an SNS topic you own. Create one in the same AWS region as your
          sending credentials, subscribe it to the webhook address below, and paste its ARN back
          here. Wend builds the rest in your AWS account — the configuration sets and their event
          destinations — because that is the part that goes wrong quietly when it is done by hand.
        </p>
      </div>

      {/*
        ONE PENDING STATE ACROSS BOTH CONTROLS — slice 24, item 21.

        The button that provisions the reports set and the form that saves the
        topic ARN act on the same record, each kept its own `pending`, and
        neither could see the other's. Pressing Set up and submitting the form
        while it ran sent two writes at the same record: the reconcile reads the
        set, the save writes the topic, and which one lands last decides what the
        row says. `working` is raised by whichever is in flight and disables the
        other — the cheapest half of the races SLICES records, and the only half
        a screen can close.
      */}
      <DeliveryReportsStatus
        deliveryReports={deliveryReports}
        onChanged={onChanged}
        busy={busy || working}
        onWorkingChange={setWorking}
      />

      {unprovisioned && (
        <Callout tone="info" title="This is usually what tracking is waiting for">
          {/*
            "Usually", and not "this is what is wrong". The browser cannot see
            whether a topic is nominated — see the file header — and a panel
            that asserted the cause would send somebody to re-paste an ARN that
            was already correct while the actual failure sat further down the
            provisioning run.
          */}
          Tracking cannot be set up in your AWS account until a topic is nominated. If you have
          already pasted an ARN here and setting up tracking is still failing, the reason is in the
          message beside the “Set up tracking in my AWS account” button rather than here.
        </Callout>
      )}

      <WebhookAddress />
      <TopicForm onChanged={onChanged} busy={busy || working} onWorkingChange={setWorking} />
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Delivery reports                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Whether this workspace's email comes back with delivery reports, and the
 * button that sets them up again.
 *
 * The button is here because a workspace that never switches tracking on still
 * needs a way to repair its delivery reports — after an IAM policy is fixed, or
 * after Wend stopped using the set because something somebody else changed on it
 * records opens. It calls `POST /tracking/delivery-reports`, which reconciles
 * the delivery reports set and nothing else. It used to call the tracking card's
 * `POST /tracking/configuration-set`, which reconciles the tracking set to the
 * switches as well — and after a re-connect with a new key, with the open switch
 * still on, that put the pixel back from a button that said nothing about it.
 *
 * It says nothing about whether reports are *arriving*. That needs the topic's
 * subscription confirmed, which this screen cannot see, and a green callout
 * implying otherwise would be the "set up means working" promise slice 19 exists
 * to stop making.
 */
function DeliveryReportsStatus({
  deliveryReports,
  onChanged,
  busy,
  onWorkingChange,
}: {
  onWorkingChange: (working: boolean) => void;
  deliveryReports: DeliveryReportsSet;
  onChanged: (next: TrackingResponse) => void;
  busy: boolean;
}) {
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<RequestFailure | null>(null);

  async function setUp(): Promise<void> {
    if (pending) return;
    setFailure(null);
    setPending(true);
    onWorkingChange(true);
    try {
      onChanged(await setUpDeliveryReports());
    } catch (error) {
      setFailure(requestFailure(error));
      await reloadAfterRefusal(onChanged);
    } finally {
      setPending(false);
      onWorkingChange(false);
    }
  }

  // The words are `readDeliveryReports`'s, so the suite can hold them without a DOM.
  const display = readDeliveryReports(deliveryReports);

  return (
    <div className="flex flex-col gap-3">
      {failure !== null && <RequestFailureAlert failure={failure} />}
      <Callout
        tone={display.tone}
        title={display.title}
        action={
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={pending}
            disabled={busy}
            onClick={() => {
              void setUp();
            }}
          >
            {pending ? 'Setting it up in AWS' : display.actionLabel}
          </Button>
        }
      >
        <p>{display.body}</p>
        {display.since !== null && (
          <p className="mt-2 font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
            Set up {formatDateTime(display.since)}
          </p>
        )}
      </Callout>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The address the topic gets subscribed to                                    */
/* -------------------------------------------------------------------------- */

/**
 * This workspace's webhook URL, fetched on render.
 *
 * Safe to fetch every time the panel mounts: `WebhookTokenService` derives the
 * URL from the tenant rather than storing it, so minting is deterministic and
 * has no side effect — reading it does not rotate anything and cannot invalidate
 * a subscription somebody already made.
 *
 * A failure here is **not** fatal to the panel. The ARN field below still works,
 * and a tenant who set their topic up in slice 9 already has the subscription
 * and does not need this string at all. So this renders its own error in place
 * and the form underneath is untouched — an error boundary around the whole
 * panel would take away a working control because a convenience failed.
 */
function WebhookAddress() {
  const { data, error, loadingFirst, reload } = useAsyncData(
    async (signal) => fetchWebhookUrl(signal),
    [],
  );

  if (loadingFirst) {
    return (
      <div className="flex items-center gap-3 rounded-base border border-line bg-white/[0.02] px-4 py-3.5 text-[0.9rem] text-muted">
        <Spinner size="sm" label={null} />
        <span role="status">Working out this workspace&rsquo;s webhook address…</span>
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="rounded-base border border-line bg-white/[0.02] px-4 py-3.5">
        <ErrorState
          error={error}
          title="Could not work out your webhook address"
          onRetry={reload}
        />
      </div>
    );
  }

  return (
    <div className="rounded-base border border-line bg-white/[0.02] px-4 py-3.5">
      <h4 className="font-mono text-[0.66rem] uppercase tracking-[0.14em] text-muted">
        Subscribe your topic to this address
      </h4>
      <p className="mt-1.5 max-w-prose text-[0.85rem] leading-relaxed text-muted">
        In the SNS console: open your topic, create a subscription, choose the{' '}
        <span className="font-mono text-[0.8rem] text-text">HTTPS</span> protocol and paste this as
        the endpoint. Amazon will send a confirmation request to it, which Wend confirms for you.
        {/*
          Said plainly rather than left to be inferred. The token in this URL is
          not a credential for the tenant's AWS account and cannot be used to
          send anything — the ingest verifies the SNS signature before it
          believes an event — but anyone holding it can post delivery events for
          this workspace, and it should not end up in a support ticket.
        */}{' '}
        Treat it as private: it is specific to this workspace, and it is not something to paste into
        a support ticket or an email.
      </p>
      <dl className="mt-3 flex flex-col">
        <CopyableValue label="Endpoint" value={data.url} labelWidth="w-24" />
      </dl>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The ARN                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The one input, validated in the browser against the same regex the API and
 * the database both apply.
 *
 * The field starts **empty on every render**, and that is the honest rendering
 * of what this screen knows rather than a missing feature: the response carries
 * no `snsTopicArn`, so a pre-filled value would have to be invented, and an
 * invented value in a field somebody is about to submit is the worst kind. The
 * description says what submitting does in both cases — first nomination and
 * repointing — because the browser cannot tell which one this is.
 *
 * Region mismatch is the failure most likely to be hit here, and it arrives
 * from the API rather than being checked locally. The browser knows the ARN's
 * region but not the credentials', so a local check would be half a check; the
 * API compares both and answers with a sentence naming each. It is rendered
 * against the field, because it is a fact about the value that was typed.
 */
function TopicForm({
  onChanged,
  busy,
  onWorkingChange,
}: {
  onChanged: (next: TrackingResponse) => void;
  busy: boolean;
  onWorkingChange: (working: boolean) => void;
}) {
  const [arn, setArn] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [failure, setFailure] = useState<RequestFailure | null>(null);
  const [pending, setPending] = useState(false);
  const [saved, setSaved] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;

    setFailure(null);
    setSaved(false);

    const result = validate(trackingTopicRequestSchema, { snsTopicArn: arn });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setPending(true);
    onWorkingChange(true);

    try {
      // `result.data.snsTopicArn` rather than the raw input: the schema trims,
      // and a trailing space pasted out of the AWS console would otherwise make
      // the browser and the API disagree about what was nominated.
      onChanged(await setTrackingTopic(result.data));
      setSaved(true);
      // Cleared on success for the reason the field starts empty: this screen
      // cannot read back what is stored, so leaving the value sitting there
      // would look like confirmation the browser has not actually received.
      setArn('');
    } catch (error) {
      const fieldErrors = apiFieldErrors(error);
      delete fieldErrors[ROOT_FIELD];
      setErrors(fieldErrors);
      setFailure(requestFailure(error));
      await reloadAfterRefusal(onChanged);
    } finally {
      setPending(false);
      onWorkingChange(false);
    }
  }

  return (
    <form
      onSubmit={(event) => {
        void onSubmit(event);
      }}
      noValidate
      className="flex max-w-xl flex-col gap-4"
    >
      {failure !== null && <RequestFailureAlert failure={failure} />}
      {errors[ROOT_FIELD] !== undefined && <FormAlert tone="error">{errors[ROOT_FIELD]}</FormAlert>}

      {saved && (
        /*
          States only what this screen actually witnessed — the API accepted the
          ARN — and not "tracking is now working", which it cannot know and
          which would be wrong whenever provisioning then fails for one of its
          other reasons.
        */
        <FormAlert tone="success">
          Saved. Amazon accepted the topic, it is in the same region as your credentials, and
          delivery reports are set up to use it. If tracking still needs setting up, use the button
          above.
        </FormAlert>
      )}

      <FormField
        label="SNS topic ARN"
        required
        description="From the topic's page in the SNS console. It has to be in the same region as your SES credentials — Amazon will not publish SES events across regions. Saving this when a topic is already nominated repoints the event destinations at the new one; events already on their way to the old topic are not re-delivered."
        error={errors.snsTopicArn}
      >
        <Input
          value={arn}
          onChange={(event) => {
            setArn(event.target.value);
            setSaved(false);
            setErrors((current) => withoutError(current, 'snsTopicArn'));
          }}
          autoComplete="off"
          spellCheck={false}
          placeholder="arn:aws:sns:ap-south-1:123456789012:wend-ses-events"
          className="font-mono text-[0.9rem]"
          disabled={pending || busy}
        />
      </FormField>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" loading={pending} disabled={busy || arn.trim() === ''}>
          {pending ? 'Saving' : 'Save topic'}
        </Button>
        <p className="text-[0.85rem] text-muted">
          Nothing is added to your messages by this. It only tells Amazon where to report events.
        </p>
      </div>
    </form>
  );
}
